import authService from '../authService';
import { ApiError, AbortError, NetworkError, TimeoutError } from './apiError';
import type {
  HttpClientConfig,
  RequestOptions,
  RequestContext,
  RequestInterceptor,
  ResponseInterceptor,
} from '../../types/services/httpClient';

const DEFAULT_TIMEOUT_MS = 30_000;
const MAX_RETRY_DELAY_MS = 30_000;

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function exponentialDelay(attempt: number, baseDelay: number): number {
  return Math.min(baseDelay * Math.pow(2, attempt), MAX_RETRY_DELAY_MS);
}

function isRetryable(error: unknown): boolean {
  if (error instanceof ApiError) return error.isServerError || error.status === 429;
  if (error instanceof NetworkError) return true;
  return false;
}

export class HttpClient {
  private readonly requestInterceptors: RequestInterceptor[] = [];
  private readonly responseInterceptors: ResponseInterceptor[] = [];

  constructor(private readonly config: HttpClientConfig = {}) {}

  addRequestInterceptor(interceptor: RequestInterceptor): this {
    this.requestInterceptors.push(interceptor);
    return this;
  }

  addResponseInterceptor(interceptor: ResponseInterceptor): this {
    this.responseInterceptors.push(interceptor);
    return this;
  }

  async get<TResponse>(url: string, options?: Omit<RequestOptions, 'body'>): Promise<TResponse> {
    return this.request<never, TResponse>(url, 'GET', options);
  }

  async post<TResponse, TBody = unknown>(url: string, options?: RequestOptions<TBody>): Promise<TResponse> {
    return this.request<TBody, TResponse>(url, 'POST', options);
  }

  async put<TResponse, TBody = unknown>(url: string, options?: RequestOptions<TBody>): Promise<TResponse> {
    return this.request<TBody, TResponse>(url, 'PUT', options);
  }

  async patch<TResponse, TBody = unknown>(url: string, options?: RequestOptions<TBody>): Promise<TResponse> {
    return this.request<TBody, TResponse>(url, 'PATCH', options);
  }

  async delete<TResponse>(url: string, options?: Omit<RequestOptions, 'body'>): Promise<TResponse> {
    return this.request<never, TResponse>(url, 'DELETE', options);
  }

  private async request<TBody, TResponse>(
    url: string,
    method: string,
    options: RequestOptions<TBody> = {}
  ): Promise<TResponse> {
    const retries = options.retries ?? this.config.retries ?? 0;
    const retryDelay = this.config.retryDelay ?? 1000;
    const timeout = options.timeout ?? this.config.timeout ?? DEFAULT_TIMEOUT_MS;
    const withAuth = options.withAuth ?? this.config.withAuth ?? false;
    const fullUrl = this.config.baseUrl ? `${this.config.baseUrl}${url}` : url;

    let lastError: unknown;

    for (let attempt = 0; attempt <= retries; attempt++) {
      if (attempt > 0) {
        await sleep(exponentialDelay(attempt - 1, retryDelay));
      }

      try {
        return await this.execute<TBody, TResponse>(fullUrl, method, options, timeout, withAuth);
      } catch (error) {
        lastError = error;
        if (!isRetryable(error) || attempt === retries) throw error;
      }
    }

    throw lastError;
  }

  private async execute<TBody, TResponse>(
    url: string,
    method: string,
    options: RequestOptions<TBody>,
    timeoutMs: number,
    withAuth: boolean
  ): Promise<TResponse> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      ...this.config.defaultHeaders,
      ...options.headers,
    };

    if (method !== 'GET' && method !== 'DELETE') {
      headers['Content-Type'] = 'application/json';
    }

    if (withAuth) {
      const token = await authService.getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    const init: RequestInit = {
      method,
      headers,
      ...(options.body !== undefined && { body: JSON.stringify(options.body) }),
      ...(options.mode !== undefined && { mode: options.mode }),
      ...(options.credentials !== undefined && { credentials: options.credentials }),
    };

    let ctx: RequestContext = { url, init };
    for (const interceptor of this.requestInterceptors) {
      ctx = await interceptor(ctx);
    }

    const controller = new AbortController();
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeoutMs);

    const externalSignal = options.signal;
    const onExternalAbort = () => controller.abort();
    externalSignal?.addEventListener('abort', onExternalAbort);

    let response: Response;
    try {
      response = await fetch(ctx.url, { ...ctx.init, signal: controller.signal });
    } catch (error) {
      if (timedOut) throw new TimeoutError(timeoutMs);
      if (error instanceof DOMException && error.name === 'AbortError') throw new AbortError();
      throw new NetworkError(
        error instanceof Error ? error.message : 'Network request failed',
        error
      );
    } finally {
      clearTimeout(timeoutId);
      externalSignal?.removeEventListener('abort', onExternalAbort);
    }

    for (const interceptor of this.responseInterceptors) {
      response = await interceptor(response);
    }

    if (!response.ok) {
      let body: unknown;
      try { body = await response.json(); } catch { /* ignore */ }
      throw new ApiError(response.status, response.statusText, body);
    }

    if (response.status === 204 || response.status === 205 || response.status === 304) return undefined as TResponse;
    return await response.json() as TResponse;
  }
}

export function createHttpClient(config?: HttpClientConfig): HttpClient {
  return new HttpClient(config);
}

export const httpClient = createHttpClient();
